import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  CreditCard,
  MapPin,
  Users,
  AlertCircle,
  AlertTriangle,
  Calendar,
  UserPlus,
  Wallet,
  ChevronLeft,
  ChevronRight,
  User,
  X,
  Search,
  Share2,
} from "lucide-react";
import { apiV2Service } from "../services/apiV2Service";
import { getCurrentUserOrganizationId } from "../screens/host-experience/host-clients/utils/hostClientsUtils";
import { getAnalytics, logEvent } from "firebase/analytics";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../shadcn/ui/dialog";
import { Button } from "../shadcn/ui/button";
import { Input } from "../shadcn/ui/input";
import { Textarea } from "../shadcn/ui/textarea";
import { Label } from "../shadcn/ui/label";
import { Badge } from "../shadcn/ui/badge";
import { Card, CardContent } from "../shadcn/ui/card";
import HUpgradeButton from "../hcomponents/HUpgradeButton";
import { Switch } from "../shadcn/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../shadcn/ui/select";
import useBookingFlow from "../hooks/useBookingFlow";
import { useUserCurrency } from "../hooks/useUserCurrency";
import toastUtils from "../utils/toast";
import { getApiMessage } from "../utils/messageHandler";
import { getBookingErrorMessage } from "../utils/bookingUtils";
import { savePendingBookingSelection, clearPendingBookingSelection } from "../utils/pendingBookingSelection";
import * as localService from "../services/localService";
import { AnalyticsEvents, AnalyticsProps, trackAnalyticsEvent } from "../utils/analytics";
import teamsService from "../services/host-apiServices/teamsService";
import { spaceManagementService } from "../services/host-apiServices/spaceManagementService";
import BookingTypeSelector from "../screens/host-experience/host-calendar/components/BookingTypeSelector";
import BookingLocationPicker from "./BookingLocationPicker";
import AddonSelector from "./addons/AddonSelector";
import {
  calculateAddonItemPrice,
  getAddonRateDisplay,
  checkAddonDateCompatibility,
} from "../utils/addonPricingUtils";
import HImageOverlay from "../hcomponents/HImageOverlay";
import { FeatureAccess } from "../utils/featureAccessControl";
import {
  getNormalizedDurationType,
  buildBookingTimeline,
  getBookingsForDate,
  isDateBooked as timelineDateBooked,
} from "../utils/bookingTimeframeUtils";
import { convertToUTC } from "../utils/timezoneUtils";
import { getEventName } from "../utils/spaceNamingUtils";
import logger from "../utils/logger";


// Stable empty references to avoid unauthorized re-renders in useEffect dependencies
const EMPTY_ARRAY = [];

/**
 * Default for the `initialSelection` prop.
 *
 * An empty object keeps the prop optional for the many existing call sites that
 * do not pass one, while still accepting a PendingBookingSelection from the
 * TypeScript caller that does. A `null` default would narrow the inferred type
 * to `null` and reject it. The restore effect treats a missing `window` as
 * "nothing to restore".
 */
const EMPTY_SELECTION = {};
const EMPTY_OBJECT = {};
const STABLE_DEFAULT_PROPS = {
  workspaces: EMPTY_ARRAY,
  availableTimeSlots: EMPTY_ARRAY,
  existingBookings: EMPTY_ARRAY,
  availabilityInstanceIds: EMPTY_ARRAY,
};

// Helper to log pricing debug events to Firebase Analytics
const logPricingDebug = (eventName, data) => {
  try {
    const analytics = getAnalytics();
    if (analytics) {
      logEvent(analytics, eventName, {
        ...data,
        timestamp: Date.now(),
        source: "ReusableBookingModal",
      });
    }
  } catch (e) {
    logger.log("Firebase Analytics not available for logging");
  }
  // Also log to console for local debugging
  logger.log(`💰 PRICING DEBUG [${eventName}]:`, data);
};

/**
 * Reusable Booking Modal - Airbnb-style booking interface
 *
 * Can be used for:
 * - Space details with booking (like in FilterProperties)
 * - Workspace booking (like in host-calendar)
 * - Any space booking scenario
 */
const ReusableBookingModal = ({
  isOpen,
  onClose,
  space,
  selectedDate,
  availableTimeSlots = EMPTY_ARRAY,
  onDateChange = () => {},
  existingBookings = EMPTY_ARRAY,
  availabilityInstanceIds = EMPTY_ARRAY,
  userCurrency = "KES",
  timeFormat = "12h",
  viewOnly = false,
  onBookingSuccess = () => {},
  onBookingError = () => {},
  // Additional props for different use cases
  showBookingSummary = true,
  showPaymentOptions = true,
  customTitle = "",
  customSubtitle = "",
  // Host calendar specific props
  isHostCalendarMode = false,
  selectedWorkspace = null,
  workspaces = EMPTY_ARRAY,
  onWorkspaceSelect = () => {},
  // Prop to control whether to redirect to reservations on success or just close modal
  closeOnSuccess = false,
  // Prop to enable booking for a customer (search & select who to book for)
  canBookForCustomer = false,
  // Selection parked by a previous visit and restored after sign-in.
  initialSelection = EMPTY_SELECTION,
}) => {
  // Use centralized booking flow hook
  const { bookingError, hasActiveSubscription, handleBookNow, handlePayNow, handleBatchBookNow, handleBatchPayNow } =
    useBookingFlow();
  const navigate = useHistory();
  const userCurrencyCode = useUserCurrency();
  const activeCurrency = isHostCalendarMode ? userCurrencyCode : space?.currency || "KES";
  const summaryCurrency = activeCurrency;

  // Booking error dialog state - must be declared before use
  const [showBookingErrorDialog, setShowBookingErrorDialog] = useState(false);
  const [bookingErrorMessage, setBookingErrorMessage] = useState("");

  // Payment loading state
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(0); // 0: Idle, 1: Securing slot, 2: Authorizing with Paystack, 3: Success
  const [isSuccessView, setIsSuccessView] = useState(false);
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  // Play services with OFFLINE-only ticket types don't use online payment —
  // they redirect users to an external URL instead. Never show Pay Now for them.
  const isPlayServiceWithOfflineTickets = (() => {
    const isPlay = space?.category?.name === "Play" || space?.isPlayService;
    if (!isPlay) return false;
    const tickets = space?.tickets || space?.spaceTickets || [];
    if (tickets.length === 0) return false;
    return tickets.every(t => t.ticketType === "OFFLINE");
  })();

  // Book button: Show for users with active subscription OR for Play services with offline tickets
  const showBookButton = hasActiveSubscription || isPlayServiceWithOfflineTickets;

  // Pay button: Show in modal when user has no subscription AND it's not an offline Play service
  const showPayButton = !hasActiveSubscription && !isPlayServiceWithOfflineTickets;

  // A signed-out visitor may pick dates, see the full price summary and leave
  // special requests, but cannot book. They get a single explicit login CTA
  // instead of Confirm/Pay, which would dead-end at an auth error. After signing
  // in this renders the normal authenticated CTA instead.
  // Read at render level to match the rest of the app — the modal is unmounted
  // by the sign-in redirect, so this is recomputed on return.
  const isSignedIn = !!(localService.getUser && localService.getUser()?.id);
  const showLoginToBookButton = !isSignedIn && showPaymentOptions;

  const [selectedStartTime, setSelectedStartTime] = useState(null);
  const [selectedEndTime, setSelectedEndTime] = useState(null);
  const [bookingSelection, setBookingSelection] = useState(null); // For BookingTypeSelector
  const [selectedInstanceId, setSelectedInstanceId] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [selectedAddons, setSelectedAddons] = useState([]);

  const [attendeeEmails, setAttendeeEmails] = useState([]);
  const [attendeeCache, setAttendeeCache] = useState({});
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [teams, setTeams] = useState([]);

  const [loadingTeams, setLoadingTeams] = useState(false);

  // Re-apply a parked selection each time the modal is opened, so a visitor
  // returning from sign-in lands on the exact date/time they had chosen.
  // Keyed on the selection identity so reopening the modal re-applies it while
  // unrelated re-renders do not reset what the visitor is currently changing.
  const appliedSelectionKeyRef = useRef(null);
  useEffect(() => {
    if (!isOpen || !initialSelection?.window) return;

    const {
      window: win,
      quantity: restoredQuantity,
      notes: restoredNotes,
      addons: restoredAddons,
      emails: restoredEmails,
    } = initialSelection;
    const key = `${win.startTime}|${win.endTime}`;
    if (appliedSelectionKeyRef.current === key) return;
    appliedSelectionKeyRef.current = key;

    const start = new Date(win.startTime);
    const end = new Date(win.endTime);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;

    logger.log("ReusableBookingModal: restoring pending booking selection", { start, end });

    if (typeof onDateChange === "function") {
      onDateChange(start);
    }

    setSelectedStartTime(start);
    setSelectedEndTime(end);
    setBookingSelection({
      startTime: start,
      endTime: end,
      startDate: win.startDate ? new Date(win.startDate) : start,
      endDate: win.endDate ? new Date(win.endDate) : end,
      durationLabel: win.durationLabel,
    });
    setIsCalendarOpen(false);

    if (restoredQuantity != null) setQuantity(restoredQuantity);
    if (restoredNotes) setNotes(restoredNotes);
    if (Array.isArray(restoredAddons) && restoredAddons.length) {
      setSelectedAddons(
        restoredAddons.map(a => ({ addonId: a.addonId, quantity: a.quantity, name: "", price: 0 }))
      );
    }
    if (Array.isArray(restoredEmails) && restoredEmails.length) {
      setAttendeeEmails(restoredEmails);
    }
  }, [isOpen, initialSelection]);

  // Customer selection (for host booking on behalf of a customer)
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // Search mode: "staff" (search org members to add as attendees) or "clients" (search customers)
  const [searchMode, setSearchMode] = useState("staff");
  const [emailInput, setEmailInput] = useState("");

  // Unified search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");

  // Multi-instance booking state
  const [quantity, setQuantity] = useState(1);
  const [maxAvailableQuantity, setMaxAvailableQuantity] = useState(1);

  // Image carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  // Internal availability data — fetched when props aren't provided
  const [fetchedInstanceIds, setFetchedInstanceIds] = useState([]);
  const [fetchedBookedSlots, setFetchedBookedSlots] = useState([]);
  const [fetchedSpaceData, setFetchedSpaceData] = useState(null);
  const [fetchingAvailability, setFetchingAvailability] = useState(true);

  // Use selectedWorkspace if in host calendar mode, otherwise use space
  const currentSpace = isHostCalendarMode ? selectedWorkspace : space;

  // MOVE category (rental vehicles) → collect a pickup location on the booking.
  // `category` may be a string ("MOVE") or an object ({ code, name }) depending on the caller,
  // so resolve defensively; match by code (authoritative), with name as a loose fallback.
  const categoryValue = currentSpace?.category;
  const categoryCode =
    typeof categoryValue === "string" ? categoryValue : categoryValue?.code || categoryValue?.name || "";
  const isMove = categoryCode.toString().toUpperCase() === "MOVE";
  // Pickup location: { label, latitude, longitude, placeId, digitalAddress, countryCode } | null
  const [pickup, setPickup] = useState(null);

  useEffect(() => {
    if (!isOpen || !currentSpace?.id) return;

    // Mock food items (in-page demo data) have no real backend identity — skip server calls.
    if (currentSpace?._isFood) {
      setFetchingAvailability(false);
      return;
    }

    let cancelled = false;
    setFetchingAvailability(true);

    // Signed-out visitors read availability through the public endpoints so they
    // can pick dates before choosing to sign in. The authenticated endpoints
    // would 401 and leave the calendar showing already-booked slots as free.
    const isAuthenticated = !!localService.getAuthToken();
    const fetchSpace = isAuthenticated
      ? spaceManagementService.getSpaceById(currentSpace.id)
      : spaceManagementService.getPublicSpaceById(currentSpace.id);
    const fetchAvailability = isAuthenticated
      ? spaceManagementService.getBookingAvailability({ spaceId: currentSpace.id })
      : spaceManagementService.getPublicBookingAvailability({ spaceId: currentSpace.id });

    Promise.all([fetchSpace.catch(() => null), fetchAvailability.catch(() => null)])
      .then(([spaceRes, availRes]) => {
        if (cancelled) return;
        const spaceData = spaceRes?.data?.data || spaceRes?.data;
        if (spaceData) {
          setFetchedSpaceData(spaceData);
        }
        const availData = availRes?.data?.data || availRes?.data;
        if (availData) {
          const booked = availData.bookedSlots || availData.bookings || [];
          setFetchedInstanceIds(availData.instanceIds || []);
          setFetchedBookedSlots(
            booked.map(slot => ({
              spaceInstanceId: slot.spaceInstanceId,
              startTime: slot.startTime,
              endTime: slot.endTime,
              status: slot.status,
            }))
          );
        }
      })
      .finally(() => {
        if (!cancelled) setFetchingAvailability(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, currentSpace?.id]);

  // Merge prop bookings with internally fetched availability data
  const effectiveAvailabilityInstanceIds =
    availabilityInstanceIds.length > 0 ? availabilityInstanceIds : fetchedInstanceIds;
  const effectiveExistingBookings = useMemo(
    () => [...(existingBookings || []), ...fetchedBookedSlots],
    [existingBookings, fetchedBookedSlots]
  );

  // Duration type from space
  const durationType = useMemo(() => getNormalizedDurationType(currentSpace), [currentSpace]);

  // Booking timeline for calendar overlays
  const bookingTimeline = useMemo(() => buildBookingTimeline(effectiveExistingBookings), [effectiveExistingBookings]);

  // Available day names for calendar
  const availableDayNames = useMemo(() => {
    if (!currentSpace?.availableSlots) return [];
    return currentSpace.availableSlots.map(slot => slot.day);
  }, [currentSpace?.availableSlots]);

  const startOfToday = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  // Calculate total instances for availability checks
  const totalInstances = useMemo(() => {
    return (
      currentSpace?.totalInstances ||
      currentSpace?.availableInstancesCount ||
      currentSpace?.numberOfInstances ||
      currentSpace?.instances?.length ||
      effectiveAvailabilityInstanceIds?.length ||
      1
    );
  }, [currentSpace, effectiveAvailabilityInstanceIds]);

  const isDateBooked = useCallback(
    date => timelineDateBooked(date, bookingTimeline, totalInstances),
    [bookingTimeline, totalInstances]
  );

  const bookingsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    return getBookingsForDate(selectedDate, bookingTimeline);
  }, [selectedDate, bookingTimeline]);

  // Get booking details from either BookingTypeSelector or legacy time selection
  const getBookingDetails = useCallback(() => {
    // If using BookingTypeSelector, use bookingSelection
    if (bookingSelection && bookingSelection.startTime && bookingSelection.endTime) {
      const durationMs = bookingSelection.endTime.getTime() - bookingSelection.startTime.getTime();
      const durationMinutes = Math.floor(durationMs / (1000 * 60));
      const durationHours = Math.ceil(durationMs / (1000 * 60 * 60));

      // Format duration label based on duration type
      let durationLabel = `${durationHours} hour${durationHours !== 1 ? "s" : ""}`;
      if (durationType === "DAILY") {
        const days = Math.ceil(durationMs / (1000 * 60 * 60 * 24));
        durationLabel = `${days} day${days !== 1 ? "s" : ""}`;
      } else if (durationType === "WEEKLY") {
        const weeks = Math.ceil(durationMs / (1000 * 60 * 60 * 24 * 7));
        durationLabel = `${weeks} week${weeks !== 1 ? "s" : ""}`;
      } else if (durationType === "MONTHLY") {
        const months = Math.max(1, Math.round(durationMs / (1000 * 60 * 60 * 24 * 30)));
        durationLabel = `${months} month${months !== 1 ? "s" : ""}`;
      }

      return {
        startTime: bookingSelection.startTime,
        endTime: bookingSelection.endTime,
        duration: durationMinutes,
        durationLabel: bookingSelection.durationLabel || durationLabel,
      };
    }

    // Fallback to old time-based selection (for backward compatibility)
    if (!selectedStartTime || !selectedEndTime || !currentSpace) return null;
    const startSlot = availableTimeSlots.find(s => s.startTime.getTime() === selectedStartTime.getTime());
    const endSlot = availableTimeSlots.find(s => s.endTime.getTime() === selectedEndTime.getTime());
    if (!startSlot || !endSlot) return null;

    const durationMs = endSlot.endTime.getTime() - startSlot.startTime.getTime();
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    const durationHours = Math.ceil(durationMs / (1000 * 60 * 60));

    // Format duration label based on duration type
    let durationLabel = `${durationHours} hour${durationHours !== 1 ? "s" : ""}`;
    if (durationType === "DAILY") {
      const days = Math.ceil(durationMs / (1000 * 60 * 60 * 24));
      durationLabel = `${days} day${days !== 1 ? "s" : ""}`;
    } else if (durationType === "WEEKLY") {
      const weeks = Math.ceil(durationMs / (1000 * 60 * 60 * 24 * 7));
      durationLabel = `${weeks} week${weeks !== 1 ? "s" : ""}`;
    } else if (durationType === "MONTHLY") {
      const months = Math.max(1, Math.round(durationMs / (1000 * 60 * 60 * 24 * 30)));
      durationLabel = `${months} month${months !== 1 ? "s" : ""}`;
    }

    return {
      startTime: startSlot.startTime,
      endTime: endSlot.endTime,
      duration: durationMinutes,
      durationLabel: durationLabel,
    };
  }, [bookingSelection, selectedStartTime, selectedEndTime, currentSpace, availableTimeSlots, durationType]);

  const bookingDetails = useMemo(() => getBookingDetails(), [getBookingDetails]);

  // Reset custom duration units when booking details change
  useEffect(() => {
    if (selectedAddons.length > 0) {
      setSelectedAddons(prev => prev.map(a => 
        a.customDurationUnits ? { ...a, customDurationUnits: null } : a
      ));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingDetails?.startTime?.getTime(), bookingDetails?.endTime?.getTime()]);

  // Instance availability for the selected time range
  const instanceAvailability = useMemo(() => {
    if (!currentSpace?.instances || !bookingDetails) return EMPTY_ARRAY;

    const isOverlap = (startA, endA, startB, endB) => startA < endB && endA > startB;

    return currentSpace.instances.map(instance => {
      const isBooked = effectiveExistingBookings.some(booking => {
        // If booking has a spaceInstanceId, only count it if it matches this instance
        // If booking has NO spaceInstanceId, it blocks ALL instances (legacy booking or general booking)
        const bookingInstanceId = booking?.spaceInstanceId ?? booking?.spaceInstance?.id;
        if (bookingInstanceId && bookingInstanceId !== instance.id) return false;

        const bookingStart = new Date(booking.startTime).getTime();
        const bookingEnd = new Date(booking.endTime).getTime();

        // Include buffer time for availability check
        const bufferMs = (currentSpace?.bufferTimeInMinutes || 0) * 60 * 1000;
        const bookingEndWithBuffer = bookingEnd + bufferMs;
        const bookingStartWithBuffer = bookingStart - bufferMs;

        return isOverlap(
          bookingStartWithBuffer,
          bookingEndWithBuffer,
          bookingDetails.startTime.getTime(),
          bookingDetails.endTime.getTime()
        );
      });

      return { instance, isBooked };
    });
  }, [currentSpace?.instances, bookingDetails, effectiveExistingBookings]);

  // True when every instance conflicts with the selected window
  const allInstancesUnavailable = useMemo(() => {
    if (!bookingDetails) return false;
    if (!currentSpace?.instances || currentSpace.instances.length === 0) return false;
    if (!instanceAvailability || instanceAvailability.length === 0) return false;
    return instanceAvailability.every(av => av.isBooked);
  }, [bookingDetails, currentSpace?.instances, instanceAvailability]);

  // Check if booking is ready (has valid dates and times)
  const isBookingReady = useMemo(() => {
    const ready = !!(
      bookingDetails &&
      bookingDetails.startTime &&
      bookingDetails.endTime &&
      (!bookingSelection || bookingSelection.isValid !== false) &&
      !allInstancesUnavailable
    );

    logger.log("[ReusableBookingModal] isBookingReady:", {
      ready,
      hasBookingDetails: !!bookingDetails,
      hasStartTime: !!bookingDetails?.startTime,
      hasEndTime: !!bookingDetails?.endTime,
      bookingSelectionValid: bookingSelection ? bookingSelection.isValid !== false : "no selection",
      allInstancesUnavailable,
    });

    return ready;
  }, [bookingDetails, bookingSelection, allInstancesUnavailable]);

  // Keep selection on an available instance; auto-select first available when time changes
  // NOTE: selectedInstanceId is intentionally NOT in the deps array to prevent infinite loops.
  // We use the functional form of setState to read the previous value instead.
  useEffect(() => {
    if (!currentSpace?.instances || currentSpace.instances.length === 0) {
      setSelectedInstanceId(null);
      return;
    }

    // If no time selected yet, keep prior selection (or first instance)
    if (!bookingDetails) {
      setSelectedInstanceId(prev => prev ?? currentSpace.instances[0].id);
      return;
    }

    setSelectedInstanceId(prev => {
      // If current selection is still available, keep it
      if (prev && instanceAvailability.some(av => av.instance.id === prev && !av.isBooked)) {
        return prev;
      }
      // Otherwise pick first available instance
      const firstAvailable = instanceAvailability.find(av => !av.isBooked)?.instance.id;
      return firstAvailable ?? currentSpace.instances[0]?.id ?? null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSpace?.instances, bookingDetails, instanceAvailability]);

  // Calculate max available quantity for the selected time
  useEffect(() => {
    if (!bookingDetails || !currentSpace) {
      setMaxAvailableQuantity(1);
      return;
    }

    // totalInstances is now defined outside via useMemo

    // If only 1 instance total, max is obviously 1
    if (totalInstances <= 1) {
      setMaxAvailableQuantity(1);
      setQuantity(1);
      return;
    }

    // Count overlapping bookings for the selected time range
    let conflictCount = 0;

    // Use instanceAvailability if we have it (most accurate for spaces with instances array)
    if (instanceAvailability && instanceAvailability.length > 0) {
      conflictCount = instanceAvailability.filter(av => av.isBooked).length;
    } else {
      // Fallback calculation if instanceAvailability not derived
      const bufferMs = (currentSpace.bufferTimeInMinutes || 0) * 60 * 1000;
      conflictCount = effectiveExistingBookings.filter(booking => {
        const bookingStart = new Date(booking.startTime).getTime();
        const bookingEnd = new Date(booking.endTime).getTime();
        // Check overlap
        // [bookingStart, bookingEnd + buffer) vs [selectedStart, selectedEnd)
        return (
          bookingDetails.startTime.getTime() < bookingEnd + bufferMs && bookingDetails.endTime.getTime() > bookingStart
        );
      }).length;
    }

    const available = Math.max(1, totalInstances - conflictCount);
    setMaxAvailableQuantity(available);

    // Reset quantity if current selection exceeds availability
    if (quantity > available) {
      setQuantity(1);
    }
  }, [bookingDetails, currentSpace, effectiveExistingBookings, instanceAvailability, effectiveAvailabilityInstanceIds]);

  // Pricing calculation based on duration type
  const spaceRate = currentSpace?.propertyPrice?.price || currentSpace?.price || currentSpace?.ratePrice || 0;
  const isMonthlyDuration = durationType === "MONTHLY";

  // 🔍 CONSOLE LOG: Space rate calculation
  useEffect(() => {
    if (currentSpace && isOpen) {
      logger.log("💵 SPACE RATE CALCULATION:", {
        spaceId: currentSpace?.id,
        spaceName: currentSpace?.name || currentSpace?.title,
        durationType,
        spaceRate,
        priceSource: currentSpace?.propertyPrice?.price
          ? "propertyPrice.price"
          : currentSpace?.price
            ? "price"
            : currentSpace?.ratePrice
              ? "ratePrice"
              : "default_0",
        rawPriceFields: {
          propertyPricePrice: currentSpace?.propertyPrice?.price,
          spacePrice: currentSpace?.price,
          ratePrice: currentSpace?.ratePrice,
        },
        activeCurrency,
      });
    }
  }, [currentSpace, isOpen, durationType, spaceRate, activeCurrency]);

  // 🔍 Log space rate source for debugging (Firebase)
  useEffect(() => {
    if (currentSpace && isOpen) {
      logPricingDebug("space_rate_calculation", {
        spaceId: currentSpace?.id,
        spaceName: currentSpace?.name || currentSpace?.title,
        durationType,
        spaceRate,
        priceSource: currentSpace?.propertyPrice?.price
          ? "propertyPrice.price"
          : currentSpace?.price
            ? "price"
            : currentSpace?.ratePrice
              ? "ratePrice"
              : "default_0",
        rawPriceFields: {
          propertyPricePrice: currentSpace?.propertyPrice?.price,
          spacePrice: currentSpace?.price,
          ratePrice: currentSpace?.ratePrice,
        },
        activeCurrency,
      });
    }
  }, [currentSpace, isOpen, durationType, spaceRate, activeCurrency]);

  // Track booking abandoned when modal is closed without completion
  const isBookingCompletedRef = React.useRef(false);

  useEffect(() => {
    if (currentSpace && isOpen) {
      isBookingCompletedRef.current = false;
      trackAnalyticsEvent(AnalyticsEvents.BOOKING_MODAL_OPENED, {
        [AnalyticsProps.BOOKING_CONTEXT]: isHostCalendarMode ? "host" : "customer",
        [AnalyticsProps.LISTING_ID]: currentSpace?.id,
        [AnalyticsProps.LISTING_NAME]: currentSpace?.name || currentSpace?.title,
        [AnalyticsProps.IS_PLAY_SERVICE]: isPlayServiceWithOfflineTickets || !!currentSpace?.isPlayService,
      });
    }

    return () => {
      if (isOpen && !isBookingCompletedRef.current && currentSpace) {
        trackAnalyticsEvent(AnalyticsEvents.BOOKING_ABANDONED, {
          [AnalyticsProps.LISTING_ID]: currentSpace?.id,
          [AnalyticsProps.LISTING_NAME]: currentSpace?.name || currentSpace?.title,
        });
      }
    };
  }, [currentSpace, isOpen, isHostCalendarMode, isPlayServiceWithOfflineTickets]);

  // Calculate pricing based on duration type
  const pricingInfo = useMemo(() => {
    if (!bookingDetails) {
      return {
        rate: spaceRate,
        rateUnit: "hour",
        quantity: 0,
        total: 0,
      };
    }

    const durationMs = bookingDetails.endTime.getTime() - bookingDetails.startTime.getTime();

    let result;
    switch (durationType) {
      case "DAILY":
        const days = Math.ceil(durationMs / (1000 * 60 * 60 * 24));
        result = {
          rate: spaceRate,
          rateUnit: "day",
          duration: days,
          total: spaceRate * days * quantity,
        };
        break;
      case "WEEKLY":
        const weeks = Math.ceil(durationMs / (1000 * 60 * 60 * 24 * 7));
        result = {
          rate: spaceRate,
          rateUnit: "week",
          duration: weeks,
          total: spaceRate * weeks * quantity,
        };
        break;
      case "MONTHLY":
        const months = Math.max(1, Math.round(durationMs / (1000 * 60 * 60 * 24 * 30)));
        const minMonths = Math.max(1, currentSpace?.minimumDuration || 1);
        const maxMonths = Math.max(minMonths, currentSpace?.maximumDuration || months);
        const finalMonths = Math.min(Math.max(months, minMonths), maxMonths);
        result = {
          rate: spaceRate,
          rateUnit: "month",
          duration: finalMonths,
          total: spaceRate * finalMonths * quantity,
        };
        break;
      case "HOURLY":
      default:
        const hours = Math.ceil(durationMs / (1000 * 60 * 60));
        result = {
          rate: spaceRate,
          rateUnit: "hour",
          duration: hours,
          total: spaceRate * hours * quantity,
        };
        break;
    }

    // Calculate itemized addon prices considering both quantity and booking duration
    const calculatedAddons = selectedAddons.map(addon => {
      const fullAddon = currentSpace?.addons?.find(a => a.id === (addon.addonId || addon.id)) || addon;
      const mergedAddon = {
        ...fullAddon,
        ...addon,
        id: fullAddon?.id || addon.addonId || addon.id,
        addonId: fullAddon?.id || addon.addonId || addon.id,
        name: fullAddon?.name || addon.name || "Add-on",
        unitPrice: fullAddon?.unitPrice ?? fullAddon?.price ?? fullAddon?.ratePrice ?? addon.unitPrice ?? addon.price ?? addon.ratePrice ?? 0,
        price: fullAddon?.unitPrice ?? fullAddon?.price ?? fullAddon?.ratePrice ?? addon.unitPrice ?? addon.price ?? addon.ratePrice ?? 0,
        duration: fullAddon?.duration || addon.duration || fullAddon?.durationType || addon.durationType || "FIXED",
        availableSlots: fullAddon?.availableSlots || addon.availableSlots || fullAddon?.availabilitySlots || addon.availabilitySlots,
        numberOfInstances: fullAddon?.numberOfInstances ?? addon.numberOfInstances,
      };
      return calculateAddonItemPrice(
        mergedAddon,
        addon.quantity || 1,
        bookingDetails,
        durationType,
        summaryCurrency,
        addon.customDurationUnits ?? null
      );
    });
    const addonsTotal = calculatedAddons.reduce((sum, item) => sum + item.totalPrice, 0);

    return {
      ...result,
      calculatedAddons,
      addonsTotal,
      total: result.total + addonsTotal,
    };
  }, [
    bookingDetails,
    durationType,
    spaceRate,
    currentSpace?.minimumDuration,
    currentSpace?.maximumDuration,
    currentSpace?.addons,
    quantity,
    selectedAddons,
    summaryCurrency,
  ]);

  const totalPrice = pricingInfo.total;

  const handleSuggestedDateSelect = useCallback(
    newDate => {
      if (!newDate) return;
      if (typeof onDateChange === "function") {
        onDateChange(newDate);
      }
    },
    [onDateChange]
  );

  const addonScheduleWarnings = useMemo(() => {
    if (!selectedAddons || selectedAddons.length === 0) return [];
    return selectedAddons
      .map(selectedAddon => {
        const fullAddon =
          currentSpace?.addons?.find(a => a.id === selectedAddon.addonId) || selectedAddon;
        const compatibility = checkAddonDateCompatibility(
          fullAddon,
          selectedDate,
          bookingDetails,
          currentSpace?.availableSlots
        );
        if (compatibility.hasConstraint && !compatibility.isCompatible) {
          return {
            addonId: fullAddon.id || selectedAddon.addonId,
            name: fullAddon.name || selectedAddon.name,
            ...compatibility,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [selectedAddons, currentSpace?.addons, selectedDate, bookingDetails, currentSpace?.availableSlots]);

  // 🔍 Log total price calculation for debugging
  useEffect(() => {
    if (bookingDetails && currentSpace && isOpen) {
      logPricingDebug("total_price_calculation", {
        spaceId: currentSpace?.id,
        spaceName: currentSpace?.name || currentSpace?.title,
        durationType,
        rate: pricingInfo.rate,
        rateUnit: pricingInfo.rateUnit,
        duration: pricingInfo.duration,
        quantity,
        totalPrice,
        startTime: bookingDetails.startTime?.toISOString(),
        endTime: bookingDetails.endTime?.toISOString(),
        activeCurrency,
      });
    }
  }, [bookingDetails, currentSpace, isOpen, durationType, pricingInfo, quantity, totalPrice, activeCurrency]);

  // Handle booking submission
  const handleSubmitBooking = async (usePayment = false) => {
    const bookingDetails = getBookingDetails();
    if (!bookingDetails) {
      toastUtils.error("Please complete the booking selection");
      return;
    }

    // Check for conflicts if using BookingTypeSelector
    if (bookingSelection && bookingSelection.isValid === false) {
      toastUtils.error("Selected time conflicts with an existing booking. Please choose a different time.");
      return;
    }

    // Guard the time range up front. Without this, an invalid range fails validation deeper in
    // the flow and gets routed into the "Pay Now" recovery dialog (meant for subscription/payment
    // failures), letting the user continue past a date error the app already flagged.
    if (
      bookingDetails.startTime &&
      bookingDetails.endTime &&
      new Date(bookingDetails.startTime) >= new Date(bookingDetails.endTime)
    ) {
      toastUtils.error("End time must be after start time");
      return;
    }

    // Get current user
    const currentUser = localService.getUser && localService.getUser();
    if (!currentUser?.id) {
      // A signed-out visitor is allowed to choose their dates and times. Park
      // the selection, send them to sign in, and restore it when they return —
      // rather than discarding their work with an error.
      const returnPath = currentSpace?.id ? `/filterprops?spaceId=${currentSpace.id}` : window.location.pathname;
      const saved = savePendingBookingSelection({
        spaceId: currentSpace?.id,
        returnPath: returnPath,
        window: {
          startTime: new Date(bookingDetails.startTime).toISOString(),
          endTime: new Date(bookingDetails.endTime).toISOString(),
          startDate: null,
          endDate: null,
          durationLabel: bookingDetails.durationLabel,
        },
        quantity,
        notes: notes.trim() || undefined,
        addons: selectedAddons.length
          ? selectedAddons.map(a => ({ addonId: a.addonId, quantity: a.quantity }))
          : undefined,
        emails: attendeeEmails.length ? [...attendeeEmails] : undefined,
      });

      if (!saved) {
        toastUtils.error("Please choose a valid date and time before continuing.");
        return;
      }

      logger.log("ReusableBookingModal: no user — saving selection and redirecting to sign in", { returnPath });
      onClose?.();
      window.location.href = `/signin?redirect=${encodeURIComponent(returnPath)}`;
      return;
    }

    // 🔍 CONSOLE LOG: handleSubmitBooking called
    logger.log("🚀 HANDLE SUBMIT BOOKING:", {
      usePayment,
      spaceId: currentSpace?.id,
      spaceName: currentSpace?.name || currentSpace?.title,
      durationType,
      quantity,
      selectedInstanceId,
      pricingInfo,
      totalPrice,
      spaceRate,
      bookingDetails: {
        startTime: bookingDetails.startTime?.toISOString(),
        endTime: bookingDetails.endTime?.toISOString(),
        durationLabel: bookingDetails.durationLabel,
      },
      currentSpacePriceFields: {
        propertyPricePrice: currentSpace?.propertyPrice?.price,
        spacePrice: currentSpace?.price,
        ratePrice: currentSpace?.ratePrice,
      },
    });

    // Determine the user to book for: selected customer or current user
    const bookingUserId = selectedCustomer
      ? (selectedCustomer.id?.toString?.() ?? String(selectedCustomer.id))
      : (currentUser.id?.toString?.() ?? String(currentUser.id));
    const bookingEmail = selectedCustomer
      ? selectedCustomer.emailAddress || selectedCustomer.email || selectedCustomer.username
      : null;

    // Build emails array: attendee emails + selected customer email + fallback to current user
    const bookingEmails = [];
    if (attendeeEmails.length > 0) {
      bookingEmails.push(...attendeeEmails);
    }
    if (selectedCustomer && bookingEmail) {
      if (!bookingEmails.includes(bookingEmail)) {
        bookingEmails.push(bookingEmail);
      }
    }
    if (bookingEmails.length === 0) {
      bookingEmails.push(currentUser.email);
    }

    // Pickup location (MOVE): send only the API's contract fields (strip UI-only countryCode).
    const pickupPayload =
      isMove && pickup
        ? {
            label: pickup.label || "",
            latitude: pickup.latitude,
            longitude: pickup.longitude,
            placeId: pickup.placeId || "",
            digitalAddress: pickup.digitalAddress || "",
          }
        : null;

    // Prepare booking payload - use convertToUTC to properly handle timezone conversion
    const payload = {
      userId: bookingUserId,
      spaceId: currentSpace.id,
      ...(selectedInstanceId ? { spaceInstanceId: selectedInstanceId } : {}),
      ...(selectedTeamId ? { teamId: selectedTeamId } : {}),
      startTime: convertToUTC(bookingDetails.startTime),
      endTime: convertToUTC(bookingDetails.endTime),
      emails: bookingEmails,
      notes: notes.trim() || null,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      addons: selectedAddons.map(a => ({ addonId: a.addonId, quantity: a.quantity })),
      ...(pickupPayload ? { pickup: pickupPayload } : {}),
    };

    // 🔍 CONSOLE LOG: Payload being sent to booking API
    logger.log("🚀 BOOKING PAYLOAD:", {
      payload,
      quantityBeingPassed: quantity,
      spaceBeingPassed: usePayment
        ? {
            id: currentSpace?.id,
            name: currentSpace?.name || currentSpace?.title,
            propertyPricePrice: currentSpace?.propertyPrice?.price,
            spacePrice: currentSpace?.price,
            ratePrice: currentSpace?.ratePrice,
            durationType: currentSpace?.durationType,
          }
        : "N/A (not payment flow)",
    });

    // For multi-quantity bookings, we need to loop
    const bookingsToCreate = [];

    // Identify available instance IDs to book
    let targetInstanceIds = [];

    if (selectedInstanceId && quantity === 1) {
      targetInstanceIds = [selectedInstanceId];
    } else {
      // Find 'quantity' number of available instances
      if (instanceAvailability && instanceAvailability.length > 0) {
        targetInstanceIds = instanceAvailability
          .filter(av => !av.isBooked)
          .slice(0, quantity)
          .map(av => av.instance.id);
      } else if (effectiveAvailabilityInstanceIds && effectiveAvailabilityInstanceIds.length > 0) {
        // Fallback if we have IDs but no full instance objects
        // We need to double check which IDs are free (re-run conflict check logic locally or assume backend validates)
        // Ideally we should filter these too. availableInstanceIds logic:
        const bufferMs = (currentSpace.bufferTimeInMinutes || 0) * 60 * 1000;
        const busyIds = new Set();
        effectiveExistingBookings.forEach(booking => {
          const bookingStart = new Date(booking.startTime).getTime();
          const bookingEnd = new Date(booking.endTime).getTime();
          if (
            bookingDetails.startTime.getTime() < bookingEnd + bufferMs &&
            bookingDetails.endTime.getTime() > bookingStart
          ) {
            if (booking.spaceInstanceId) busyIds.add(booking.spaceInstanceId);
          }
        });
        targetInstanceIds = effectiveAvailabilityInstanceIds.filter(id => !busyIds.has(id)).slice(0, quantity);
      }
    }

    // Prepare payloads
    if (targetInstanceIds.length > 0) {
      targetInstanceIds.forEach(instId => {
        bookingsToCreate.push({
          ...payload,
          spaceInstanceId: instId,
        });
      });
    } else {
      // Fallback for spaces without instances or if logic fails (e.g. quantity=1, auto assign)
      // Just push one payload, backend usually auto-assigns if spaceInstanceId is null/missing
      // but here we are explicit. If we can't find instances, we default to standard payload (single)
      if (quantity > 1) {
        toastUtils.error("Could not identify available units for multi-booking. Please try booking one by one.");
        return;
      }
      bookingsToCreate.push(payload);
    }

    if (bookingsToCreate.length === 0) {
      toastUtils.error("Space Not Available", {
        description: "No available instances for the selected time. Please pick another slot.",
        id: "space-not-available",
      });
      return;
    }

    // Track booking initiation (funnel step: modal → submit)
    trackAnalyticsEvent(AnalyticsEvents.BOOKING_INITIATED, {
      [AnalyticsProps.BOOKING_CONTEXT]: isHostCalendarMode ? "host" : "customer",
      [AnalyticsProps.PAYMENT_MODE]: usePayment ? "pay" : "book",
      [AnalyticsProps.BOOKING_COUNT]: bookingsToCreate.length,
      [AnalyticsProps.LISTING_ID]: currentSpace?.id,
      [AnalyticsProps.IS_PLAY_SERVICE]: isPlayServiceWithOfflineTickets || !!currentSpace?.isPlayService,
    });

    try {
      // Ensure space is available for payment flow
      if (usePayment && !currentSpace) {
        toastUtils.error("Space information is missing. Please try again.");
        return;
      }

      let result;

      if (bookingsToCreate.length > 1) {
        // Use batch handlers for multiple units
        result = await (usePayment ? handleBatchPayNow : handleBatchBookNow)(bookingsToCreate, {
          space: usePayment ? currentSpace : undefined,
          quantity: quantity,
          onSuccess: response => {
            // Success handler
          },
          onError: error => {
            // Error handler
          },
        });
      } else {
        // Standard single booking flow
        result = await (usePayment ? handlePayNow : handleBookNow)(bookingsToCreate[0], {
          space: usePayment ? currentSpace : undefined,
          quantity: quantity,
          onSuccess: response => {
            // Success handler
          },
          onError: error => {
            // Error handler
          },
        });
      }

      // If successful
      if (result?.success) {
        isBookingCompletedRef.current = true;
        // The parked selection has served its purpose — drop it so a later visit
        // to this space does not resurrect a completed booking.
        clearPendingBookingSelection();
        if (onBookingSuccess) {
          onBookingSuccess(result, usePayment ? "create booking with pending payment" : "create booking");
        }

        // Track successful booking (funnel completion)
        trackAnalyticsEvent(AnalyticsEvents.BOOKING_COMPLETED, {
          [AnalyticsProps.BOOKING_CONTEXT]: isHostCalendarMode ? "host" : "customer",
          [AnalyticsProps.PAYMENT_MODE]: usePayment ? "pay" : "book",
          [AnalyticsProps.BOOKING_ID]: result?.bookingId || result?.booking?.id || result?.id,
          [AnalyticsProps.BOOKING_COUNT]: bookingsToCreate.length,
          [AnalyticsProps.TOTAL_AMOUNT]: totalPrice,
          [AnalyticsProps.CURRENCY]: activeCurrency || currentSpace?.currency || "GHS",
          addons_count: pricingInfo?.calculatedAddons?.length || selectedAddons?.length || 0,
          addons_total: pricingInfo?.addonsTotal || 0,
          [AnalyticsProps.IS_PLAY_SERVICE]: isPlayServiceWithOfflineTickets || !!result?.isPlayService,
        });

        // Success feedback
        if (usePayment) {
          if (result.paymentResult?.success) {
            toastUtils.success("Payment initiated", {
              description: "Complete payment in the new tab to confirm your booking.",
            });
          }
        } else {
          toastUtils.success("Booking successful!", {
            description: "Your reservation has been confirmed.",
          });
          onClose(); // Close modal immediately for regular bookings
        }

        // Handle PLAY service tickets if present (highest priority redirect)
        if (result.isPlayService && result.ticketUrl) {
          setTimeout(() => {
            window.location.href = result.ticketUrl;
          }, 1500);
          return result;
        }

        // For host calendar mode or when closeOnSuccess is true, just close the modal instead of redirecting
        if (isHostCalendarMode || closeOnSuccess) {
          onClose();
        } else {
          // Standard redirection to reservations for other booking scenarios
          setTimeout(() => {
            navigate.push("/reservations");
          }, 1500);
        }

        return result;
      }

      if (!result?.success) {
        // Extract error message properly - handle nested objects/strings
        let errorMessage = "Failed to create booking";

        if (result.error) {
          // If error is already a string, use it
          if (typeof result.error === "string") {
            errorMessage = result.error;
          } else if (typeof result.error === "object") {
            // If error is an object, try to extract message using getBookingErrorMessage
            // Create a mock error object structure that getBookingErrorMessage expects
            const mockError = {
              response: {
                data: {
                  message: result.error.message || result.error.error || result.error,
                },
              },
              message: result.error.message || result.error.error || JSON.stringify(result.error),
            };
            errorMessage = getBookingErrorMessage(mockError);
          }
        }

        // Track failed booking (funnel drop-off)
        trackAnalyticsEvent(AnalyticsEvents.BOOKING_FAILED, {
          [AnalyticsProps.BOOKING_CONTEXT]: isHostCalendarMode ? "host" : "customer",
          [AnalyticsProps.PAYMENT_MODE]: usePayment ? "pay" : "book",
          [AnalyticsProps.ERROR_REASON]: errorMessage,
          [AnalyticsProps.IS_PLAY_SERVICE]: isPlayServiceWithOfflineTickets || !!currentSpace?.isPlayService,
        });

        // Always show error in toast for better UX
        const isAvailabilityError =
          errorMessage.toLowerCase().includes("no available instance") ||
          errorMessage.toLowerCase().includes("not available") ||
          errorMessage.toLowerCase().includes("unavailable");

        const isPastTimeError =
          errorMessage.toLowerCase().includes("cannot book a time in the past") ||
          errorMessage.toLowerCase().includes("book a time in the past") ||
          errorMessage.toLowerCase().includes("time in the past");

        if (isAvailabilityError) {
          // Use consistent message format - only show description if it's different from the title
          const cleanMessage = errorMessage.replace(/^(space\s+)?not\s+available[:\s]*/i, "").trim();
          toastUtils.error("Space Not Available", {
            description:
              cleanMessage || "This space is not available at the selected time. Please choose a different time slot.",
            id: "space-not-available", // Use consistent ID to prevent duplicate toasts
          });
        } else if (isPastTimeError) {
          toastUtils.error("Invalid Time Selection", {
            description: "You cannot book a time in the past. Please select a future date and time.",
            id: "past-time-error",
          });
        } else {
          // Suppress subscription-related toast as requested
          if (errorMessage && errorMessage.includes("No suitable subscription found")) {
            logger.log("DEBUG: Suppressing subscription-related toast in modal manual call");
          } else {
            toastUtils.error(errorMessage || "Booking failed. Please try again.");
          }
        }

        // For payment flow, show toast, reset loading, and close modal if closeOnSuccess is true
        if (usePayment) {
          setIsPaymentLoading(false);
          if (isHostCalendarMode || closeOnSuccess) {
            onClose();
          } else {
            // Redirect to reservations page after payment flow
            setTimeout(() => {
              navigate.push("/reservations");
            }, 1500);
          }
          return result;
        }

        // For non-payment flows, only show error dialog for certain errors (not past time or availability errors)
        // Past time and availability errors should only show toast, no dialog
        if (!isPastTimeError && !isAvailabilityError) {
          setBookingErrorMessage(errorMessage);
          setShowBookingErrorDialog(true);
        }
        return result;
      }

      // Check if this is a PLAY service with ticket URL (handled in hook)
      if (result?.isPlayService && result?.ticketUrl) {
        toastUtils.success("Booking created successfully! Redirecting...");
        setTimeout(() => {
          window.location.href = result.ticketUrl;
        }, 1500);
        return result; // Don't close modal, redirecting
      }

      if (usePayment) {
        setCheckoutStep(2); // Step 2: Paystack Authorization
        if (result?.paymentResult?.success) {
          toastUtils.success("Payment initiated", {
            description: "Complete payment in the new tab to confirm your booking.",
          });
        }

        // Show self-drawing SVG checkmark success view inside modal matching landing theme
        setConfirmedBooking(result?.booking || result);
        setCheckoutStep(3);
        setIsSuccessView(true);
        setIsPaymentLoading(false);

        // For host calendar mode or when closeOnSuccess is true, close modal after short delay
        setTimeout(() => {
          if (isHostCalendarMode || closeOnSuccess) {
            onClose();
            setIsSuccessView(false);
          } else {
            navigate.push("/reservations");
            setIsSuccessView(false);
          }
        }, 3200);

        return result;
      }
      // Note: Regular booking success messages are handled by parent via onBookingSuccess callback
      onClose();

      return result;
    } catch (error) {
      logger.error("Booking error:", error);

      // Use getBookingErrorMessage to properly extract nested error messages
      const errorMessage = getBookingErrorMessage(error) || getApiMessage(error, "create booking");

      // Track failed booking (funnel drop-off from catch path)
      trackAnalyticsEvent(AnalyticsEvents.BOOKING_FAILED, {
        [AnalyticsProps.BOOKING_CONTEXT]: isHostCalendarMode ? "host" : "customer",
        [AnalyticsProps.PAYMENT_MODE]: usePayment ? "pay" : "book",
        [AnalyticsProps.ERROR_REASON]: errorMessage,
        [AnalyticsProps.IS_PLAY_SERVICE]: isPlayServiceWithOfflineTickets || !!currentSpace?.isPlayService,
      });

      // Check error types to show appropriate toast and prevent duplicates
      const isAvailabilityError =
        errorMessage.toLowerCase().includes("no available instance") ||
        errorMessage.toLowerCase().includes("not available") ||
        errorMessage.toLowerCase().includes("unavailable");

      const isPastTimeError =
        errorMessage.toLowerCase().includes("cannot book a time in the past") ||
        errorMessage.toLowerCase().includes("book a time in the past") ||
        errorMessage.toLowerCase().includes("time in the past");

      // Always show error in toast for better UX
      if (isAvailabilityError) {
        const cleanMessage = errorMessage.replace(/^(space\s+)?not\s+available[:\s]*/i, "").trim();
        toastUtils.error("Space Not Available", {
          description:
            cleanMessage || "This space is not available at the selected time. Please choose a different time slot.",
          id: "space-not-available", // Use consistent ID to prevent duplicate toasts
        });
      } else if (isPastTimeError) {
        toastUtils.error("Invalid Time Selection", {
          description: "You cannot book a time in the past. Please select a future date and time.",
          id: "past-time-error",
        });
      } else {
        // Suppress subscription-related toast as requested
        if (errorMessage && errorMessage.includes("No suitable subscription found")) {
          logger.log("DEBUG: Suppressing subscription-related toast in modal catch manual call");
        } else {
          toastUtils.error(errorMessage || "Booking failed. Please try again.");
        }
      }

      // For payment flow, just reset loading
      if (usePayment) {
        setIsPaymentLoading(false);
      }

      // For non-payment flows, only show error dialog for certain errors (not past time or availability errors)
      if (!usePayment && !isPastTimeError && !isAvailabilityError) {
        setBookingErrorMessage(errorMessage);
        setShowBookingErrorDialog(true);
      }

      if (onBookingError) {
        onBookingError(error, usePayment ? "create booking with pending payment" : "create booking");
      }
      return { success: false, error: errorMessage };
    }
  };

  // Handle Pay Now from error dialog
  const handlePayNowFromDialog = async () => {
    setIsPaymentLoading(true);
    setShowBookingErrorDialog(false);
    try {
      await handleSubmitBooking(true); // true = use payment
    } finally {
      setIsPaymentLoading(false);
    }
  };

  // Unified search result selection
  const handleSelectSearchResult = user => {
    const email = user.email || user.emailAddress || user.username;
    if (!email) {
      toastUtils.error("User does not have an email address");
      return;
    }

    if (searchMode === "clients" && !selectedCustomer) {
      setSelectedCustomer(user);
      setSearchTerm("");
      setSearchResults([]);
    } else {
      if (attendeeEmails.includes(email)) {
        toastUtils.warning("User already added");
        return;
      }
      setAttendeeCache(prev => ({ ...prev, [email]: user }));
      setAttendeeEmails(prev => [...prev, email]);
      setSearchTerm("");
      setSearchResults([]);
    }
  };

  const handleEmailKeyDown = e => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmed = emailInput.trim();
      if (!trimmed) return;

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(trimmed)) {
        if (attendeeEmails.includes(trimmed)) {
          toastUtils.warning("Email already added");
          return;
        }
        setAttendeeEmails(prev => [...prev, trimmed]);
        setEmailInput("");
      }
    }
  };

  // Fetch user's teams when modal opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    const fetchTeams = async () => {
      try {
        setLoadingTeams(true);

        const currentUser = localService.getUser && localService.getUser();
        // Handle nested user structure { user: {...}, access_token: "..." }
        const actualUser = currentUser?.user ?? currentUser;

        // Check if user has access to teams feature
        if (!FeatureAccess.hasTeamsAccess(actualUser)) {
          if (!cancelled) {
            setTeams([]);
            setLoadingTeams(false);
          }
          return;
        }

        // Block teams for host property management orgs outside the host calendar
        const orgType = actualUser?.organization?.type?.code;
        if (!window.location.pathname.startsWith("/host/calendar") && orgType === "HOST_PROPERTY_MANAGEMENT") {
          if (!cancelled) {
            setTeams([]);
            setLoadingTeams(false);
          }
          return;
        }

        let userId = null;

        if (actualUser?.id) {
          userId = typeof actualUser.id === "string" ? actualUser.id : String(actualUser.id);
        }

        const searchParams = {
          pageNumber: 0,
          pageSize: 100, // Get all teams
          paged: true,
        };

        if (userId) {
          searchParams.userId = userId;
        }

        const response = await teamsService.searchTeams(searchParams);

        if (!cancelled && response?.status === 200 && response?.data?.content) {
          setTeams(response.data.content);
        }
      } catch (error) {
        // Silently fail - teams are optional
        logger.error("Error fetching teams:", error);
        if (!cancelled) setTeams([]);
      } finally {
        if (!cancelled) setLoadingTeams(false);
      }
    };

    fetchTeams();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Search effect — searches customers or staff depending on searchMode toggle
  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.trim().length < 1) {
      setSearchResults([]);
      setSearchError("");
      return;
    }

    const isClientsMode = canBookForCustomer && searchMode === "clients" && !selectedCustomer;
    const isStaffMode = !isClientsMode && FeatureAccess.hasAttendeeAccess(localService.getUser());

    if (!isClientsMode && !isStaffMode) {
      setSearchResults([]);
      setSearchError("");
      return;
    }

    let cancelled = false;

    const timeoutId = setTimeout(async () => {
      setSearchLoading(true);
      setSearchError("");

      const organizationId = getCurrentUserOrganizationId();
      if (!organizationId) {
        if (!cancelled) {
          setSearchError("Organization ID not found. Please refresh and try again.");
          setSearchLoading(false);
        }
        return;
      }

      try {
        const res = await apiV2Service.searchUsers({
          q: searchTerm.trim(),
          limit: 10,
          status: "ACTIVE",
          ...(isClientsMode ? { mode: "CLIENTS" } : { organizationId }),
        });
        if (!cancelled) {
          let users = [];
          if (Array.isArray(res?.data?.data)) {
            users = res.data.data;
          } else if (Array.isArray(res?.data)) {
            users = res.data;
          }
          const currentUser = localService.getUser();
          const currentUserEmail = currentUser?.email || currentUser?.emailAddress || currentUser?.username;
          if (currentUserEmail) {
            users = users.filter(u => (u.email || u.emailAddress || u.username) !== currentUserEmail);
          }
          setSearchResults(users);
        }
      } catch (err) {
        logger.error("Search error:", err);
        if (!cancelled) {
          setSearchError("Failed to search");
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [searchTerm, canBookForCustomer, searchMode, selectedCustomer]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedStartTime(null);
      setSelectedEndTime(null);
      setBookingSelection(null);
      setNotes("");
      setAttendeeEmails([]);
      setAttendeeCache({});
      setSelectedTeamId(null);
      setTeams([]);
      setSelectedCustomer(null);
      setSearchTerm("");
      setSearchResults([]);
      setSearchMode("staff");
      setEmailInput("");
      setShowBookingErrorDialog(false);
      setBookingErrorMessage("");
      setCurrentImageIndex(0); // Reset image carousel
      setIsOverlayOpen(false); // Reset overlay
      setPickup(null); // Reset pickup location
    }
  }, [isOpen]);

  if (!isOpen || !currentSpace) return null;

  const images = currentSpace.media?.images || currentSpace.images || [];
  // Get all image URLs for carousel
  const imageUrls = images.map(img => (typeof img === "string" ? img : img?.resourceUrl || img?.url)).filter(Boolean);
  const amenities = currentSpace.amenities || [];

  // Image carousel navigation
  const handlePrevImage = () => {
    setCurrentImageIndex(prev => (prev === 0 ? imageUrls.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex(prev => (prev === imageUrls.length - 1 ? 0 : prev + 1));
  };
  const displayLocation =
    typeof currentSpace.location === "string" && currentSpace.location.trim()
      ? currentSpace.location
      : currentSpace.location?.address ||
        currentSpace.location?.name ||
        currentSpace.buildingAddress?.address ||
        (currentSpace.cityOrTown
          ? `${currentSpace.cityOrTown}${currentSpace.country ? `, ${currentSpace.country}` : ""}`
          : null) ||
        "Location not specified";

  const handleShareSpace = async () => {
    if (!currentSpace?.id) return;
    const shareUrl = `${window.location.origin}/space/${currentSpace.id}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: currentSpace?.name || currentSpace?.title || "Space Details",
          text: `Check out ${currentSpace?.name || currentSpace?.title || "this space"} on Spacia`,
          url: shareUrl,
        });
        return;
      } catch (err) {
        // Fallback to clipboard if share was aborted or failed
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toastUtils.success("Space link copied to clipboard!");
    } catch (e) {
      toastUtils.error("Failed to copy link");
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={open => !isPaymentLoading && onClose()}>
        <DialogContent className="max-w-5xl p-0 gap-0 max-h-[92vh] flex flex-col overflow-hidden rounded-2xl border-none shadow-2xl">
          {/* Header */}
          <DialogHeader className="px-6 py-5 border-b border-slate-100 flex-shrink-0 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-xl font-bold text-slate-900">
                  {customTitle || "Review and book"}
                </DialogTitle>
                {customSubtitle ? (
                  <p className="text-xs text-slate-500 mt-1">{customSubtitle}</p>
                ) : (
                  <p className="text-xs text-slate-500 mt-1">Review details and secure your reservation slot</p>
                )}
              </div>
              {currentSpace?.id && (
                <div className="flex items-center gap-2 mr-6">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleShareSpace}
                    className="flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 border-slate-200"
                    title="Share Space"
                  >
                    <Share2 className="w-3.5 h-3.5 text-[#017bef]" />
                    <span>Share</span>
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto min-h-0 bg-white">
            {isSuccessView ? (
              <div className="flex flex-col items-center justify-center p-12 text-center space-y-6 min-h-[400px]">
                {/* Self-drawing Animated SVG Checkmark */}
                <div className="relative w-20 h-20 flex items-center justify-center">
                  <svg className="w-20 h-20 text-emerald-500" viewBox="0 0 52 52">
                    <circle className="animate-stroke-circle" cx="26" cy="26" r="23" fill="none" stroke="currentColor" strokeWidth="2.5" />
                    <path className="animate-stroke-check" fill="none" stroke="currentColor" strokeWidth="3" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                  </svg>
                </div>

                <div className="space-y-2">
                  <span className="inline-block text-[11px] font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-full uppercase tracking-wider">
                    Booking Confirmed
                  </span>
                  <h3 className="text-2xl font-extrabold text-slate-900">
                    Payment Successful!
                  </h3>
                  <p className="text-sm text-slate-500 max-w-md mx-auto">
                    Your reservation for <strong className="text-slate-800">{currentSpace?.title || currentSpace?.name || "Space"}</strong> has been confirmed with Paystack.
                  </p>
                </div>

                {confirmedBooking?.bookingCode && (
                  <div className="bg-slate-50 border border-slate-100 rounded-xl px-5 py-3 text-xs">
                    <span className="text-slate-400 font-semibold block uppercase tracking-wider text-[10px]">Booking Reference</span>
                    <span className="text-base font-extrabold text-blue-600 tracking-wide">{confirmedBooking.bookingCode}</span>
                  </div>
                )}

                <div className="text-xs text-slate-400 animate-pulse pt-4">
                  Redirecting to your reservation details...
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2">
              {/* Left Column: Visuals and Specs */}
              <div className="p-6 border-b lg:border-b-0 lg:border-r border-slate-100 space-y-6">
                {/* Space Image Carousel */}
                {imageUrls.length > 0 && (
                  <div className="relative rounded-xl overflow-hidden bg-slate-100 group aspect-video">
                    <img
                      src={imageUrls[currentImageIndex]}
                      alt={currentSpace.name || "Space"}
                      className="w-full h-full object-cover cursor-pointer transition-transform duration-300 group-hover:scale-105"
                      onClick={() => setIsOverlayOpen(true)}
                    />

                    {imageUrls.length > 1 && (
                      <>
                        <button
                          onClick={handlePrevImage}
                          className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full backdrop-blur-sm transition-all z-20"
                          aria-label="Previous image"
                        >
                          ‹
                        </button>

                        <button
                          onClick={handleNextImage}
                          className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full backdrop-blur-sm transition-all z-20"
                          aria-label="Next image"
                        >
                          ›
                        </button>

                        {/* Dot indicator row */}
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1 z-20">
                          {imageUrls.map((_, index) => (
                            <button
                              key={index}
                              onClick={() => setCurrentImageIndex(index)}
                              className={`transition-all duration-200 rounded-full ${
                                index === currentImageIndex
                                  ? "w-4 h-1.5 bg-white shadow"
                                  : "w-1.5 h-1.5 bg-white/55 hover:bg-white/80"
                              }`}
                              aria-label={`Go to image ${index + 1}`}
                            />
                          ))}
                        </div>

                        {/* Image Counter */}
                        <div className="absolute top-3 right-3 bg-black/55 text-white text-[10px] font-semibold px-2 py-1 rounded-full z-20 backdrop-blur-sm">
                          {currentImageIndex + 1} / {imageUrls.length}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Info and tags */}
                <div className="space-y-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1.5">
                      {currentSpace.hostDisplayImage && (
                        <img
                          src={currentSpace.hostDisplayImage}
                          alt={currentSpace.hostDisplayName || "Host"}
                          className="w-5 h-5 rounded-full object-cover shrink-0 ring-1 ring-slate-100"
                          onError={e => {
                            e.target.style.display = "none";
                          }}
                        />
                      )}
                      <h3 className="text-lg font-bold text-slate-800">
                        {currentSpace.title || currentSpace.name || getEventName(currentSpace) || "Space"}
                      </h3>
                    </div>

                    <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                      {currentSpace.capacity && (
                        <span className="inline-flex items-center gap-1 bg-slate-100 px-2.5 py-1 rounded-md font-medium text-slate-600">
                          <Users className="w-3.5 h-3.5 text-slate-400" />
                          {currentSpace.capacity} people
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1 bg-slate-100 px-2.5 py-1 rounded-md font-medium text-slate-600">
                        <MapPin className="w-3.5 h-3.5 text-slate-400" />
                        {displayLocation}
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  {currentSpace.description && (
                    <div className="border-t border-slate-100 pt-4">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                        About this space
                      </h4>
                      <p className="text-sm text-slate-600 leading-relaxed font-normal">{currentSpace.description}</p>
                    </div>
                  )}

                  {/* Amenities */}
                  {amenities.length > 0 && (
                    <div className="border-t border-slate-100 pt-4">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Amenities</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {amenities.slice(0, 8).map(amenity => (
                          <span
                            key={amenity.id || amenity.name}
                            className="text-xs bg-slate-50 text-slate-600 px-2.5 py-1 rounded-md border border-slate-100 font-medium"
                          >
                            {amenity.label || amenity.name}
                          </span>
                        ))}
                        {amenities.length > 8 && (
                          <span className="text-xs text-slate-400 px-2 py-1 font-medium">
                            +{amenities.length - 8} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column: Interactive Booking Form */}
              <div className="p-6 bg-slate-50/50 border-t lg:border-t-0 lg:border-l border-slate-150 space-y-5">
                {/* Workspace selector if host calendar */}
                {isHostCalendarMode && workspaces.length > 0 && (
                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 block">
                      Workspace
                    </Label>
                    <Select
                      value={selectedWorkspace?.id?.toString()}
                      onValueChange={value => {
                        const workspace = workspaces.find(w => w.id.toString() === value);
                        if (workspace && onWorkspaceSelect) {
                          onWorkspaceSelect(workspace);
                        }
                      }}
                    >
                      <SelectTrigger className="w-full bg-slate-50 border-slate-200">
                        <SelectValue placeholder="Select a workspace" />
                      </SelectTrigger>
                      <SelectContent>
                        {workspaces.map(workspace => (
                          <SelectItem key={workspace.id} value={workspace.id.toString()}>
                            {workspace.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Availability Picker */}
                <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm space-y-4">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Select Date & Time</h4>
                  <BookingTypeSelector
                    durationType={durationType}
                    selectedDate={selectedDate}
                    availableTimeSlots={availableTimeSlots}
                    space={currentSpace}
                    minimumDuration={currentSpace?.minimumDuration || 1}
                    maximumDuration={currentSpace?.maximumDuration || 24}
                    bufferTimeInMinutes={currentSpace?.bufferTimeInMinutes || 0}
                    existingBookings={effectiveExistingBookings}
                    availabilityInstanceIds={effectiveAvailabilityInstanceIds}
                    spaceId={currentSpace.id}
                    timeFormat={timeFormat}
                    onSelectionChange={setBookingSelection}
                    onDateChange={onDateChange}
                    instances={currentSpace?.instances || EMPTY_ARRAY}
                    disabled={false}
                    initialSelection={initialSelection}
                  />

                  {/* Pickup location (MOVE / rental vehicles) */}
                  {isMove && (
                    <div className="pt-2 border-t border-slate-100">
                      <BookingLocationPicker value={pickup} onChange={setPickup} label="Pickup location" />
                    </div>
                  )}

                  {/* Quantity Selector */}
                  {maxAvailableQuantity > 1 && (
                    <div className="pt-3 border-t border-slate-100">
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 block">
                        Number of Units
                      </Label>
                      <Select value={quantity.toString()} onValueChange={val => setQuantity(parseInt(val))}>
                        <SelectTrigger className="w-full bg-slate-50 border-slate-200">
                          <SelectValue placeholder="Select quantity" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: maxAvailableQuantity }, (_, i) => i + 1).map(num => (
                            <SelectItem key={num} value={num.toString()}>
                              {num} Unit{num > 1 ? "s" : ""} ({activeCurrency} {spaceRate.toLocaleString()} per unit)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <div className="text-[10px] text-slate-400 mt-1.5 font-medium">
                        {maxAvailableQuantity} units available for this time slot
                      </div>
                    </div>
                  )}
                </div>

                {/* Team Selection */}
                {teams.length > 0 &&
                  FeatureAccess.hasTeamsAccess(localService.getUser()?.user ?? localService.getUser()) && (
                    <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                      <Label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 block">
                        Book for Team (Optional)
                      </Label>
                      <Select
                        value={selectedTeamId ? selectedTeamId.toString() : "none"}
                        onValueChange={value => setSelectedTeamId(value === "none" ? null : parseInt(value))}
                        disabled={loadingTeams}
                      >
                        <SelectTrigger className="w-full bg-slate-50 border-slate-200">
                          <SelectValue placeholder="Select a team (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No team (Personal booking)</SelectItem>
                          {teams.map(team => (
                            <SelectItem key={team.id} value={team.id.toString()}>
                              {team.name}
                              {team.description && (
                                <span className="text-xs text-slate-400 ml-2">({team.description})</span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                {/* Attendees */}
                <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm space-y-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-400 block">
                    Attendees <span className="font-normal text-slate-400 text-[10px] lowercase">(optional)</span>
                  </Label>
                  <div className="space-y-3 relative">
                    <Input
                      type="email"
                      placeholder="Add attendee by email..."
                      value={emailInput}
                      onChange={e => setEmailInput(e.target.value)}
                      onKeyDown={handleEmailKeyDown}
                      className="bg-slate-50 border-slate-200 text-sm focus-visible:ring-blue-400"
                    />

                    {canBookForCustomer ? (
                      <div className="flex items-center justify-between text-xs bg-slate-50 p-2 rounded-lg border border-slate-100">
                        <span className="font-medium text-slate-500">
                          {searchMode === "staff" ? "Search staff directory" : "Search customer database"}
                        </span>
                        <Switch
                          checked={searchMode === "clients"}
                          onCheckedChange={checked => setSearchMode(checked ? "clients" : "staff")}
                        />
                      </div>
                    ) : localService.getUser()?.organization?.type?.code === "HOST_PROPERTY_MANAGEMENT" ? (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Book for customers</span>
                        <HUpgradeButton size="sm" />
                      </div>
                    ) : null}

                    <div className="relative">
                      <Input
                        type="text"
                        placeholder={
                          searchMode === "clients" && !selectedCustomer
                            ? "Search client name or email..."
                            : "Search team member name..."
                        }
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full pr-8 text-xs bg-slate-50 border-slate-200 focus-visible:ring-blue-400"
                      />
                      <Search className="absolute right-2.5 top-2.5 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                      {searchTerm && (
                        <button
                          type="button"
                          onClick={() => {
                            setSearchTerm("");
                            setSearchResults([]);
                          }}
                          className="absolute right-8 top-2.5 text-slate-400 hover:text-slate-600"
                          aria-label="Clear search"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>

                    {searchLoading && <div className="text-xs text-slate-400 animate-pulse">Searching...</div>}
                    {searchError && <div className="text-xs text-red-500">{searchError}</div>}

                    {/* Results Dropdown */}
                    {searchResults.length > 0 && (
                      <div className="overflow-y-auto absolute z-50 w-full max-h-44 bg-white rounded-xl border border-slate-200 shadow-xl mt-1">
                        {searchResults.map((user, index) => {
                          const displayName =
                            user.firstName && user.lastName
                              ? `${user.firstName} ${user.lastName}`
                              : user.name || user.username || "Unknown User";
                          const email = user.email || user.emailAddress || user.username;
                          const isAdded =
                            searchMode === "clients" && !selectedCustomer
                              ? selectedCustomer?.id === user.id
                              : attendeeEmails.includes(email);

                          return (
                            <div
                              key={user.id || index}
                              className={`flex items-center gap-2.5 px-3.5 py-2.5 cursor-pointer border-b border-slate-50 last:border-b-0 transition-colors ${
                                isAdded ? "bg-slate-50 opacity-60" : "hover:bg-blue-50/50"
                              }`}
                              onClick={() => !isAdded && handleSelectSearchResult(user)}
                            >
                              {user.imageUrl ? (
                                <img
                                  src={user.imageUrl}
                                  alt={displayName}
                                  className="w-8 h-8 rounded-full border border-slate-100 object-cover shrink-0"
                                  onError={e => {
                                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(
                                      user.firstName || "U"
                                    )}&background=E0F2FE&color=0369A1`;
                                  }}
                                />
                              ) : (
                                <div className="w-8 h-8 bg-blue-50 rounded-full border border-blue-100 flex items-center justify-center shrink-0">
                                  <User className="w-3.5 h-3.5 text-blue-500" />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="text-xs font-semibold text-slate-800 truncate">{displayName}</div>
                                <div className="text-[10px] text-slate-400 truncate">{email}</div>
                              </div>
                              {isAdded && <div className="text-[10px] text-blue-500 font-semibold">Added</div>}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {selectedCustomer && (
                      <div className="flex items-center justify-between p-3 bg-blue-50/60 border border-blue-100 rounded-xl">
                        <div className="min-w-0">
                          <div className="text-xs font-bold text-blue-900 truncate">
                            {selectedCustomer.firstName} {selectedCustomer.lastName}
                          </div>
                          <div className="text-[10px] text-blue-600 truncate">{selectedCustomer.emailAddress}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedCustomer(null);
                            setSearchResults([]);
                          }}
                          className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold"
                        >
                          Change
                        </button>
                      </div>
                    )}

                    {/* Added attendees chips */}
                    {attendeeEmails.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {attendeeEmails.map(email => {
                          const user = attendeeCache[email];
                          const displayName = user
                            ? `${user.firstName || ""} ${user.lastName || ""}`.trim() || user.name || email
                            : email;

                          return (
                            <span
                              key={email}
                              className="inline-flex items-center gap-1 text-[11px] bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full border border-slate-200"
                            >
                              <span className="truncate max-w-[120px]">{displayName}</span>
                              <button
                                type="button"
                                onClick={() => setAttendeeEmails(prev => prev.filter(e => e !== email))}
                                className="text-slate-400 hover:text-red-500 font-bold shrink-0 ml-0.5"
                              >
                                &times;
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                {/* Addons Selection */}
                {currentSpace?.addons && currentSpace.addons.length > 0 && (
                  <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                    <AddonSelector
                      addons={currentSpace.addons}
                      selectedAddons={selectedAddons}
                      onChange={setSelectedAddons}
                      currency={activeCurrency}
                      selectedDate={selectedDate || bookingDetails?.startTime}
                      bookingDetails={bookingDetails}
                      spaceAvailableSlots={currentSpace?.availableSlots}
                      onSelectDate={handleSuggestedDateSelect}
                      spaceDurationType={durationType}
                    />
                  </div>
                )}

                {/* Notes */}
                <div className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 block">
                    Special Requests (Optional)
                  </Label>
                  <Textarea
                    placeholder="E.g. room temperature preference, extra monitors, catering requests..."
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    rows={2}
                    className="bg-slate-50 border-slate-200 text-sm focus-visible:ring-blue-400"
                  />
                </div>

                {/* Billing Summary invoice card */}
                {showBookingSummary && !isHostCalendarMode && bookingDetails && (
                  <div className="bg-slate-50 border border-slate-200/90 rounded-xl p-4 space-y-3">
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Price Summary
                    </div>
                    <div className="space-y-2 text-xs">
                      {(durationType === "DAILY" || durationType === "WEEKLY" || durationType === "MONTHLY") && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-500">Check-in</span>
                            <span className="font-medium text-slate-800">
                              {bookingDetails.startTime.toLocaleString("en-US", {
                                month: "short",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: timeFormat !== "24h",
                              })}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-slate-500">Check-out</span>
                            <span className="font-medium text-slate-800">
                              {bookingDetails.endTime.toLocaleString("en-US", {
                                month: "short",
                                day: "numeric",
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: timeFormat !== "24h",
                              })}
                            </span>
                          </div>
                        </>
                      )}

                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Duration</span>
                        <span className="font-medium text-slate-800">{bookingDetails.durationLabel}</span>
                      </div>

                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Base rate</span>
                        <span className="text-slate-700">
                          {summaryCurrency} {(pricingInfo.rate || 0).toLocaleString()} / {pricingInfo.rateUnit} &times;{" "}
                          {quantity} unit{quantity !== 1 ? "s" : ""}
                        </span>
                      </div>

                      {pricingInfo?.calculatedAddons && pricingInfo.calculatedAddons.length > 0 && (
                        <div className="space-y-1.5 pt-2 border-t border-slate-200/80 text-xs">
                          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            Add-ons
                          </div>
                          {pricingInfo.calculatedAddons.map((addonItem, index) => {
                            const isTimeBased = addonItem.duration && addonItem.duration !== "FIXED" && addonItem.duration !== "ONE_TIME" && addonItem.duration !== "NONE";
                            return (
                              <div key={index} className="flex justify-between items-start gap-2">
                                <div>
                                  <span className="font-medium text-slate-800">{addonItem.name}</span>
                                  <span className="text-slate-500 block text-[11px]">
                                    {summaryCurrency} {(addonItem.unitPrice || 0).toLocaleString()} / {addonItem.rateUnit} &times; {addonItem.quantity} unit{addonItem.quantity !== 1 ? "s" : ""}
                                    {isTimeBased && (
                                      <span> &times; {addonItem.durationUnits} {addonItem.durationUnitLabel}</span>
                                    )}
                                    {addonItem.customUnits != null && addonItem.customUnits < addonItem.maxUnits && (
                                      <span className="text-[10px] text-slate-400"> (of {addonItem.maxUnits} {addonItem.durationUnitLabel} booked)</span>
                                    )}
                                  </span>
                                </div>
                                <span className="font-medium text-slate-800 whitespace-nowrap">
                                  {summaryCurrency} {(addonItem.totalPrice || 0).toLocaleString()}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Add-on schedule warning notice in summary card */}
                      {addonScheduleWarnings.length > 0 && (
                        <div className="mt-2.5 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900 space-y-1.5">
                          <div className="flex items-center gap-1.5 font-semibold text-amber-800">
                            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                            <span>Add-on Schedule Notice</span>
                          </div>
                          {addonScheduleWarnings.map((warn, i) => (
                            <div key={i} className="text-[11px] text-amber-800 leading-tight">
                              <span className="font-medium text-amber-950">{warn.name}</span> is only available on{" "}
                              <span className="font-semibold">{warn.availableDaysSummary}</span>.
                            </div>
                          ))}
                          {addonScheduleWarnings[0]?.nextAvailableDates?.length > 0 && (
                            <div className="pt-1.5 border-t border-amber-200/80 flex flex-wrap items-center gap-1.5">
                              <span className="text-[11px] text-amber-800 font-medium">Switch date:</span>
                              {addonScheduleWarnings[0].nextAvailableDates.map((item, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => handleSuggestedDateSelect(item.date)}
                                  className="px-2 py-0.5 bg-white hover:bg-amber-100 border border-amber-300 rounded text-[11px] font-medium text-amber-900 shadow-xs transition-colors cursor-pointer active:scale-95"
                                  title={`Switch booking date to ${item.dayLabel}, ${item.formatted}`}
                                >
                                  {item.formatted}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      <div className="border-t border-slate-200/80 my-2 pt-2" />

                      <div className="flex justify-between items-baseline text-sm font-semibold text-slate-900">
                        <span>Total Price</span>
                        <span className="text-base font-bold text-slate-900">
                          {summaryCurrency} {totalPrice.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Footer - Fixed CTA bar */}
          <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 bg-white">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              {/* Back out label / status indicators */}
              <div className="text-xs text-slate-400">
                {isPaymentLoading ? (
                  <span className="flex items-center gap-1.5">
                    <div className="w-3 h-3 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin" />
                    Processing your reservation...
                  </span>
                ) : (
                  <span>Cancel at any time before confirmation</span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 items-center justify-end">
                {showLoginToBookButton ? (
                  <Button
                    onClick={() => handleSubmitBooking(true)}
                    disabled={!isBookingReady}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg shadow-blue-200 transition-all duration-150 hover:shadow-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Login to Book
                  </Button>
                ) : (
                  <>
                    {showBookButton && showPaymentOptions && (
                      <Button
                        onClick={() => handleSubmitBooking(isPlayServiceWithOfflineTickets)}
                        disabled={!isBookingReady}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg shadow-blue-200 transition-all duration-150 hover:shadow-blue-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Confirm Booking
                      </Button>
                    )}

                    {showPayButton && showPaymentOptions && (
                      <Button
                        onClick={() => handleSubmitBooking(true)}
                        disabled={!isBookingReady}
                        className="bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg shadow-green-200 transition-all duration-150 hover:shadow-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        Pay and Confirm
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showBookingErrorDialog} onOpenChange={setShowBookingErrorDialog}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Wallet className="w-6 h-6 text-blue-600" />
            </div>

            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-900">Payment Required</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{bookingErrorMessage}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 w-full">
              <Button variant="outline" onClick={() => setShowBookingErrorDialog(false)} className="flex-1">
                Try Again
              </Button>
              <Button
                onClick={handlePayNowFromDialog}
                disabled={
                  isPaymentLoading || !bookingDetails || allInstancesUnavailable || bookingSelection?.isValid === false
                }
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {isPaymentLoading ? (
                  <>
                    <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Pay Now
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Overlay */}
      {isOverlayOpen && (
        <HImageOverlay
          isOpen={isOverlayOpen}
          onClose={() => setIsOverlayOpen(false)}
          images={imageUrls}
          currentIndex={currentImageIndex}
          onIndexChange={setCurrentImageIndex}
        />
      )}
    </>
  );
};

export default ReusableBookingModal;
